<template>
  <div class="specialized-license-types">
    <!-- Main Table Card -->
    <v-card class="mt-4">
      <v-card-title class="d-flex align-center justify-space-between">
        <span>จัดการประเภทใบอนุญาตประกอบวิชาชีพเฉพาะ</span>
        <v-btn color="primary" @click="openDialog('create')" dark>
          <v-icon left>mdi-plus</v-icon>เพิ่มใหม่
        </v-btn>
      </v-card-title>

      <!-- Search and Filter Section -->
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="searchText"
              label="ค้นหา"
              placeholder="ค้นหารหัส หรือ ชื่อประเภทใบอนุญาต"
              outlined
              dense
              clearable
              prepend-inner-icon="mdi-magnify"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
              v-model="filterActive"
              :items="[
                { text: 'ทั้งหมด', value: null },
                { text: 'ใช้งาน', value: 1 },
                { text: 'ไม่ใช้งาน', value: 0 }
              ]"
              item-text="text"
              item-value="value"
              label="สถานะการใช้งาน"
              outlined
              dense
            />
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Data Table -->
      <v-data-table
        :headers="headers"
        :items="filteredLicenses"
        :loading="loading"
        @update:options="loadLicenses"
        class="elevation-1"
        dense
      >
        <!-- License Type Code Column -->
        <template v-slot:item.license_type_code="{ item }">
          <v-chip small label>{{ item.license_type_code }}</v-chip>
        </template>

        <!-- License Type Name Column -->
        <template v-slot:item.license_type_name="{ item }">
          <div class="font-weight-medium">{{ item.license_type_name }}</div>
          <div class="text-caption grey--text">{{ item.license_issuer }}</div>
        </template>

        <!-- Status Column -->
        <template v-slot:item.is_active="{ item }">
          <v-chip
            :color="item.is_active ? 'success' : 'grey'"
            text-color="white"
            small
            label
          >
            {{ item.is_active ? "ใช้งาน" : "ไม่ใช้งาน" }}
          </v-chip>
        </template>

        <!-- Created Date Column -->
        <template v-slot:item.created_at="{ item }">
          <span class="text-caption">{{ formatDate(item.created_at) }}</span>
        </template>

        <!-- Action Column -->
        <template v-slot:item.actions="{ item }">
          <div class="d-flex gap-2">
            <v-btn
              icon
              small
              color="primary"
              @click="openDialog('edit', item)"
              title="แก้ไข"
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              color="error"
              @click="confirmDelete(item)"
              title="ลบ"
            >
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </div>
        </template>

        <!-- No data slot -->
        <template v-slot:no-data>
          <div class="text-center py-6">
            <v-icon large class="grey--text">mdi-database-off</v-icon>
            <p class="grey--text mt-2">ไม่พบข้อมูลประเภทใบอนุญาต</p>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Form Dialog -->
    <v-dialog v-model="dialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <span>{{ dialogTitle }}</span>
          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-form ref="form" @submit.prevent="submitForm">
            <!-- License Type Code -->
            <v-text-field
              v-model="formData.license_type_code"
              label="รหัสประเภทใบอนุญาต"
              placeholder="เช่น ACC-001"
              outlined
              dense
              class="mb-3"
              :rules="rules.license_type_code"
              :disabled="dialogMode === 'edit'"
              counter="20"
              maxlength="20"
            />

            <!-- License Type Name -->
            <v-text-field
              v-model="formData.license_type_name"
              label="ชื่อประเภทใบอนุญาต"
              placeholder="ระบุชื่อประเภทใบอนุญาต"
              outlined
              dense
              class="mb-3"
              :rules="rules.license_type_name"
              counter="255"
              maxlength="255"
            />

            <!-- License Issuer -->
            <v-text-field
              v-model="formData.license_issuer"
              label="หน่วยงานออกใบอนุญาต"
              placeholder="ระบุชื่อหน่วยงาน"
              outlined
              dense
              class="mb-3"
              counter="255"
              maxlength="255"
            />

            <!-- License Description -->
            <v-textarea
              v-model="formData.license_description"
              label="รายละเอียดใบอนุญาต"
              placeholder="ระบุรายละเอียด"
              outlined
              dense
              rows="4"
              class="mb-3"
              counter="500"
              maxlength="500"
            />

            <!-- Status -->
            <v-switch
              v-model="formData.is_active"
              label="ใช้งาน"
              class="mb-2"
              :true-value="1"
              :false-value="0"
            />
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn @click="closeDialog" outlined>ยกเลิก</v-btn>
          <v-btn
            @click="submitForm"
            color="primary"
            :loading="submitting"
            depressed
          >
            {{
              dialogMode === "create" ? "เพิ่มเข้าระบบ" : "บันทึกการเปลี่ยนแปลง"
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="confirmDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon large class="error--text mr-2">mdi-alert-circle</v-icon>
          <span>ยืนยันการลบ</span>
        </v-card-title>

        <v-card-text>
          <p>คุณแน่ใจว่าต้องการลบประเภทใบอนุญาต?</p>
          <p class="font-weight-medium">
            {{ deleteItemData.license_type_name }}
          </p>
          <p class="text-caption grey--text">การกระทำนี้ไม่สามารถย้อนกลับได้</p>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn @click="confirmDeleteDialog = false" outlined>ยกเลิก</v-btn>
          <v-btn
            @click="deleteItem"
            color="error"
            :loading="submitting"
            depressed
          >
            ยืนยันการลบ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar.show" :timeout="snackbar.timeout" top>
      <v-icon left :color="snackbar.color">{{ snackbar.icon }}</v-icon>
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: "SpecializedLicenseTypesComponentDialog",
  data() {
    return {
      // API Configuration
      ApiKey: "HRvec2021",

      // Table data
      licenses: [],
      headers: [
        { text: "รหัส", value: "license_type_code", width: "100px" },
        {
          text: "ชื่อประเภทใบอนุญาต",
          value: "license_type_name",
          width: "300px"
        },
        { text: "สถานะ", value: "is_active", width: "100px" },
        { text: "วันที่สร้าง", value: "created_at", width: "120px" },
        { text: "จัดการ", value: "actions", sortable: false, width: "100px" }
      ],
      tableOptions: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: []
      },
      totalItems: 0,

      // Search and filter
      searchText: "",
      filterActive: null,

      // Dialog states
      dialog: false,
      dialogMode: "create", // 'create' or 'edit'
      submitting: false,
      loading: false,

      // Form data
      formData: {
        license_type_id: null,
        license_type_code: "",
        license_type_name: "",
        license_issuer: "",
        license_description: "",
        is_active: 1
      },

      // Delete confirmation
      confirmDeleteDialog: false,
      deleteItemData: {},

      // Validation rules
      rules: {
        license_type_code: [
          v => !!v || "รหัสประเภทใบอนุญาตจำเป็นต้องระบุ",
          v => /^[A-Z0-9\-]*$/.test(v) || "รหัสต้องเป็นตัวพิมพ์ใหญ่และตัวเลข"
        ],
        license_type_name: [
          v => !!v || "ชื่อประเภทใบอนุญาตจำเป็นต้องระบุ",
        ]
      },

      // Snackbar
      snackbar: {
        show: false,
        message: "",
        color: "success",
        icon: "mdi-check-circle",
        timeout: 3000
      }
    };
  },

  computed: {
    dialogTitle() {
      return this.dialogMode === "create"
        ? "เพิ่มประเภทใบอนุญาตใหม่"
        : "แก้ไขประเภทใบอนุญาต";
    },

    filteredLicenses() {
      return this.licenses.filter(item => {
        // Filter by search text
        const searchMatch =
          !this.searchText ||
          item.license_type_code
            .toLowerCase()
            .includes(this.searchText.toLowerCase()) ||
          item.license_type_name
            .toLowerCase()
            .includes(this.searchText.toLowerCase());

        // Filter by active status
        const statusMatch =
          this.filterActive === null || item.is_active === this.filterActive;

        return searchMatch && statusMatch;
      });
    }
  },

 async mounted() {
   await this.loadLicenses();
  },

  methods: {
    /**
     * Load licenses from server
     */
    async loadLicenses() {
      this.loading = true;
      try {
        const response = await this.$http.post(
          "specialized_license_types.php",
          {
            ApiKey: this.ApiKey
          }
        );
          
        const data = response.data
        this.licenses = Array.isArray(data) ? data : [];
        this.totalItems = this.licenses.length;
      } catch (error) {
        console.error("Error loading licenses:", error);
        this.showSnackbar(
          "เกิดข้อผิดพลาดในการโหลดข้อมูล",
          "error",
          "mdi-alert-circle"
        );
      } finally {
        this.loading = false;
      }
    },

    /**
     * Open dialog for create or edit
     */
    openDialog(mode, item = null) {
      this.dialogMode = mode;

      if (mode === "create") {
        this.formData = {
          license_type_id: null,
          license_type_code: "",
          license_type_name: "",
          license_issuer: "",
          license_description: "",
          is_active: 1
        };
      } else if (mode === "edit" && item) {
        this.formData = { ...item };
      }

      this.dialog = true;
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }
      });
    },

    /**
     * Close dialog
     */
    closeDialog() {
      this.dialog = false;
      if (this.$refs.form) {
        this.$refs.form.reset();
      }
    },

    /**
     * Submit form - create or update
     */
    async submitForm() {
      if (!this.$refs.form.validate()) {
        this.showSnackbar(
          "กรุณาตรวจสอบข้อมูลที่ป้อนเข้า",
          "warning",
          "mdi-alert"
        );
        return;
      }

      this.submitting = true;

      try {
        const endpoint =
          this.dialogMode === "create"
            ? "specialized_license_types.insert.php"
            : "specialized_license_types.update.php";

        const payload = {
          ApiKey: this.ApiKey,
          ...this.formData,
          updated_at: new Date()
            .toISOString()
            .slice(0, 19)
            .replace("T", " ")
        };

        const response = await this.$http.post(endpoint, payload);
        console.log(response.data)
        const result = response.data

        if (result.status) {
          const message =
            this.dialogMode === "create"
              ? "เพิ่มประเภทใบอนุญาตเรียบร้อยแล้ว"
              : "บันทึกการเปลี่ยนแปลงเรียบร้อยแล้ว";
          this.showSnackbar(message, "success", "mdi-check-circle");
          this.closeDialog();
          await this.loadLicenses();
        } else {
          this.showSnackbar(
            "ไม่สามารถบันทึกข้อมูลได้",
            "error",
            "mdi-alert-circle"
          );
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        this.showSnackbar(
          "เกิดข้อผิดพลาดในการบันทึกข้อมูล",
          "error",
          "mdi-alert-circle"
        );
      } finally {
        this.submitting = false;
      }
    },

    /**
     * Show confirmation dialog before delete
     */
    confirmDelete(item) {
      this.deleteItemData = { ...item };
      this.confirmDeleteDialog = true;
    },

    /**
     * Delete item
     */
    async deleteItem() {
      this.submitting = true;

      try {
        const response = await this.$http.post("specialized_license_types.delete.php", {
          ApiKey: this.ApiKey,
          license_type_id: this.deleteItemData.license_type_id
        });

        const result = response.data

        if (result.status) {
          this.showSnackbar(
            "ลบประเภทใบอนุญาตเรียบร้อยแล้ว",
            "success",
            "mdi-check-circle"
          );
          this.confirmDeleteDialog = false;
          await this.loadLicenses();
        } else {
          this.showSnackbar(
            "ไม่สามารถลบข้อมูลได้",
            "error",
            "mdi-alert-circle"
          );
        }
      } catch (error) {
        console.error("Error deleting item:", error);
        this.showSnackbar(
          "เกิดข้อผิดพลาดในการลบข้อมูล",
          "error",
          "mdi-alert-circle"
        );
      } finally {
        this.submitting = false;
      }
    },

    /**
     * Show snackbar notification
     */
    showSnackbar(message, type = "success", icon = "mdi-check-circle") {
      this.snackbar = {
        show: true,
        message,
        color:
          type === "error"
            ? "error"
            : type === "warning"
            ? "warning"
            : "success",
        icon,
        timeout: 3000
      };
    },

    /**
     * Format date
     */
    formatDate(dateString) {
      if (!dateString) return "-";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("th-TH", {
          year: "numeric",
          month: "short",
          day: "numeric"
        });
      } catch {
        return dateString;
      }
    }
  }
};
</script>

<style scoped>
.specialized-license-types {
  padding: 0;
}

.gap-2 {
  gap: 8px;
  display: flex;
}

:deep(.v-data-table) {
  background: transparent;
}

:deep(.v-data-table__wrapper) {
  overflow-x: auto;
}

:deep(.v-chip) {
  font-weight: 500;
}

:deep(.text-caption) {
  line-height: 1.4;
}
</style>
